import { IonIcon, IonSpinner } from '@ionic/react';
import {
  checkmarkCircle,
  cloudOfflineOutline,
  filmOutline,
  imageOutline,
  playCircleOutline,
  trashOutline,
} from 'ionicons/icons';
import { useTranslation } from 'react-i18next';
import type { MediaAsset } from '@ai-media/shared';
import { assetPreviewUrl, describeAsset, isBusyAsset, isVideoAsset } from '@/lib/media';

interface MediaCardProps {
  asset: MediaAsset;
  selected?: boolean;
  selectable?: boolean;
  busy?: boolean;
  onSelect?: (asset: MediaAsset) => void;
  onDelete?: (asset: MediaAsset) => void;
  /** Shows a per-card progress overlay while a render is in flight. */
  progressLabel?: string | null;
}

/** One tile of the media library: poster, kind badge, state and actions. */
export default function MediaCard({
  asset,
  selected = false,
  selectable = false,
  busy = false,
  onSelect,
  onDelete,
  progressLabel,
}: MediaCardProps) {
  const { t } = useTranslation();
  const preview = assetPreviewUrl(asset);
  const pending = isBusyAsset(asset);

  return (
    <div
      className={`media-tile${selected ? ' media-tile--selected' : ''}`}
      onClick={selectable && !pending ? () => onSelect?.(asset) : undefined}
      role={selectable ? 'button' : undefined}
    >
      <div className="media-tile__thumb">
        {preview ? (
          <img src={preview} alt={asset.prompt} loading="lazy" />
        ) : (
          <div className="media-tile__fallback">
            <IonIcon icon={isVideoAsset(asset) ? filmOutline : imageOutline} />
          </div>
        )}

        {isVideoAsset(asset) && preview && !pending && (
          <IonIcon className="media-tile__play" icon={playCircleOutline} />
        )}

        {pending && (
          <div className="media-tile__overlay">
            <IonSpinner name="crescent" />
            <span>{progressLabel ?? t('media.rendering')}</span>
          </div>
        )}

        {asset.status === 'failed' && !preview && (
          <div className="media-tile__overlay media-tile__overlay--error">
            <IonIcon icon={cloudOfflineOutline} />
            <span>{t('media.status.failed')}</span>
          </div>
        )}

        {selected && <IonIcon className="media-tile__check" icon={checkmarkCircle} />}

        <span className="media-tile__kind">
          <IonIcon icon={isVideoAsset(asset) ? filmOutline : imageOutline} />
          {t(`media.kind.${asset.kind}`)}
        </span>
      </div>

      <div className="media-tile__meta">
        <span className="media-tile__title" title={asset.prompt}>
          {asset.prompt}
        </span>
        <span className="media-tile__sub">{describeAsset(asset)}</span>
      </div>

      <div className="media-tile__actions">
        {asset.degraded && (
          <span className="media-tile__flag" title={t('media.degradedHint')}>
            {t('media.degraded')}
          </span>
        )}
        {asset.usage_count > 0 && (
          <span className="media-tile__flag">{t('media.usedTimes', { count: asset.usage_count })}</span>
        )}
        {onDelete && (
          <button
            type="button"
            className="media-tile__delete"
            disabled={busy}
            onClick={(event) => {
              event.stopPropagation();
              onDelete(asset);
            }}
            aria-label={t('common.delete')}
          >
            <IonIcon icon={trashOutline} />
          </button>
        )}
      </div>
    </div>
  );
}
